import * as Sentry from "@sentry/react";
import { startsWith, trim } from "lodash/fp";

import logger from "@readai/common/logger";

const parseTeamsJoinInfo = (info: string): string | undefined => {
  // First, attempt to find the link by id
  const template = document.createElement("template");
  template.innerHTML = info;

  let joinLink = (
    template.content.getElementById(
      "meet_invite_block.action.join_link",
    ) as HTMLAnchorElement | null
  )?.href;

  // If that fails, fall back to the first link we can find
  if (!joinLink) {
    joinLink = template.content.querySelector("a")?.href;
  }

  // Log if we think the link could be invalid. If the link is
  // invalid, the BE will return a 422 or 400
  if (
    joinLink &&
    (!startsWith("https://teams.microsoft.com", joinLink) ||
      !joinLink.includes("context"))
  ) {
    Sentry.captureMessage("unrecognized teams join link");
  }

  return joinLink;
};

export const parseJoinInfo = (info: string): string => {
  const trimmedValue = trim(info);

  // Teams join info copy content is, for some idiotic reason, an HTML element
  if (startsWith("<div", trimmedValue)) {
    // if parsing the join info failed, just show the full value and people can
    // hopefully figure out how to pull the link out on their own
    return parseTeamsJoinInfo(trimmedValue) ?? trimmedValue;
  }

  return trimmedValue;
};

export const parseReportInviteFromUrl = (url: string | null) => {
  let redirectUrl: URL | null = null;
  let reportInvite: string | null = null;
  if (!url) return null;

  try {
    const redirectString = url?.startsWith("http")
      ? url
      : `${window.location.origin}${url}`;

    // First try and parse without decoding
    try {
      redirectUrl = new URL(redirectString);
    } catch {
      // If that fails, try decoding
      redirectUrl = new URL(decodeURIComponent(redirectString));
    }

    reportInvite = redirectUrl?.searchParams.get("mInvite") ?? null;
  } catch {
    logger.warn("Invalid URL", { url });
  }
  return reportInvite;
};

type ReportType = "topic" | "meeting";

// Parses a report Invite into the report type, reportId (session ID or topic
// ID) and token needed to accept access invite
export const parseReportInfoFromInvite = (
  /**
   * format [topic:]reportId:token
   */
  reportInvite: string | null,
): {
  reportType: ReportType;
  reportInviteReportId: string | null;
  reportInviteToken: string | null;
} => {
  let reportType: ReportType = "meeting";
  let reportInviteReportId: string | null = null;
  let reportInviteToken: string | null = null;

  if (!reportInvite) {
    return { reportType, reportInviteReportId, reportInviteToken };
  }

  // decode the invite in case it was encoded
  const decodedReportInvite = decodeURIComponent(reportInvite);

  const parts = decodedReportInvite.split(":");

  if (parts[0] === "topic") {
    reportType = "topic";
    [reportInviteReportId, reportInviteToken] = [
      parts[1],
      parts.slice(2).join(":"),
    ];
  } else {
    reportType = "meeting";
    [reportInviteReportId, reportInviteToken] = [
      parts[0],
      parts.slice(1).join(":"),
    ];
  }

  return { reportType, reportInviteReportId, reportInviteToken };
};
